import { useCallback, useEffect, useRef, useState } from "react";
import api, { isCanceled } from "../lib/api";

/**
 * Loads a paginated list and keeps it in step with `filter`.
 *
 * Every page previously fired a bare request on each filter change with no
 * abort and no cleanup, so two quick changes could let the slower first
 * response land last and repaint the table with stale rows.
 *
 * @param {string} path     endpoint, relative to the api baseURL
 * @param {object} filter   query parameters; a change triggers a refetch
 * @param {object} options
 * @param {(err) => void} options.onError   called for genuine failures only
 * @param {boolean}       options.enabled   skip fetching while false
 */
export default function useListData(path, filter, { onError, enabled = true } = {}) {
    const [data, setData] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [reloadToken, setReloadToken] = useState(0);

    // Kept in a ref so a caller passing an inline arrow doesn't re-trigger
    // the effect on every render.
    const onErrorRef = useRef(onError);
    onErrorRef.current = onError;

    const reload = useCallback(() => setReloadToken((n) => n + 1), []);

    const serialized = JSON.stringify(filter);

    useEffect(() => {
        if (!enabled) return undefined;

        const controller = new AbortController();
        let active = true;

        setLoading(true);

        api.get(path, { params: JSON.parse(serialized), signal: controller.signal })
            .then((res) => {
                if (!active) return;
                setData(Array.isArray(res.data.data) ? res.data.data : []);
                setTotal(Number(res.data.total) || 0);
            })
            .catch((err) => {
                if (!active || isCanceled(err)) return;
                setData([]);
                setTotal(0);
                if (onErrorRef.current) onErrorRef.current(err);
            })
            .finally(() => {
                if (active) setLoading(false);
            });

        // Aborts the in-flight request when the filter changes again or the
        // page unmounts.
        return () => {
            active = false;
            controller.abort();
        };
    }, [path, serialized, reloadToken, enabled]);

    return { data, total, loading, reload, setData };
}
